'use client';

import classnames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import React, { useMemo, useState } from 'react';

import { HighlightItem } from '@/app/[locale]/(default-layout)/specials/_components/SpecialsHeroContent';

import { ShowingInfoModal } from '@/components/showing-details/ShowingInfoModal';
import TicketLink from '@/components/ticket-shape/TicketLink';

import { getShowingPropertiesLanguage, getShowingTechnologies } from '@cineamo/ui/src/helpers/showing-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconCircleSolid16 } from '@cineamo/ui/src/icons/ic-circleSolid';
import { IconInfo16 } from '@cineamo/ui/src/icons/ic-info';
import { IconTicket2_24 } from '@cineamo/ui/src/icons/ic-ticket';

import MobileOrDesktopRenderer from '@cineamo/composables/src/MobileOrDesktopRenderer';

import { useCinemaRooms } from '@/providers/CinemaRoomProvider';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useShowingTags } from '@/providers/ShowingTagProvider';

type HighlightListItemProps = Readonly<{
    highlight: HighlightItem;
    isActiveInHero: boolean;
    onClick?: () => void;
}>;

export default function HighlightListItem({
    highlight,
    isActiveInHero,
    onClick
}: HighlightListItemProps): React.JSX.Element {
    const domainConfiguration = useDomainConfiguration();
    const { getCinemaRoomById } = useCinemaRooms();
    const { getShowingTagById } = useShowingTags();
    const t = useTranslations();
    const locale = useLocale();
    const format = useFormat({ timeZone: domainConfiguration.timeZone });
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

    const { showing, content } = highlight;

    const weekday = format.dateTime.formatWeekday(showing.startDatetime, 'short').replace('.', '');
    const date = format.dateTime.formatDayMonth(showing.startDatetime);
    const time = format.dateTime.formatDate_HHmm24(showing.startDatetime);

    const cinemaRoom = getCinemaRoomById(showing.cinemaRoomId);
    const cinemaRoomName = cinemaRoom?.name;

    const is3D = showing.isThreeDimensional;
    const languageInfo = getShowingPropertiesLanguage(showing, locale);
    const technologies = getShowingTechnologies(showing).map((tech: string) =>
        t(`showtimes.showing-info.technology.${tech}`)
    );

    const screeningInfoParts: string[] = [
        cinemaRoomName,
        is3D ? '3D' : undefined,
        ...languageInfo,
        ...technologies
    ].filter((it): it is string => !!it);

    const showingTags = useMemo(() => {
        return (
            showing.showingTagIds?.map((tagId) => {
                return getShowingTagById(tagId) ?? null;
            }) ?? []
        );
    }, [showing.showingTagIds, getShowingTagById]);

    return (
        <div
            className={classnames(
                'text-left w-full cursor-pointer',
                'grid gap-x-12 gap-y-4',
                'grid-cols-[94px_minmax(0,1fr)_auto] lg:grid-cols-[100px_minmax(0,1fr)_auto]',
                'grid-rows-[auto_auto]'
            )}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.();
                }
            }}>
            {/* Date & Time column */}
            <div className="col-start-1 row-start-1 flex flex-col items-start justify-center pt-2">
                <span className="text-body-bold-14 truncate w-full">
                    {weekday}, {date}
                </span>
                <span className="flex items-center gap-6">
                    <span className="text-body-bold-14">{time}</span>
                    {isActiveInHero && <IconCircleSolid16 className="size-8 text-[var(--primary-color)]" />}
                </span>
            </div>

            {/* Movie info column */}
            <div className="col-start-2 row-start-1 flex flex-col items-start justify-center pr-4">
                <span className="text-body-bold-16 truncate w-full">{content.title}</span>
                <div className="flex items-center gap-6 text-body-12 flex-wrap">
                    {screeningInfoParts.map((part, index) => (
                        <React.Fragment key={index}>
                            {index > 0 && <span className="opacity-50">|</span>}
                            <span className="whitespace-nowrap">{part}</span>
                        </React.Fragment>
                    ))}
                    <button
                        type="button"
                        className="shrink-0 cursor-pointer opacity-50 hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsInfoModalOpen(true);
                        }}>
                        <IconInfo16 className="size-14" />
                    </button>
                </div>
            </div>

            {/* Tickets button column */}
            <div className="col-start-3 row-start-1 self-start">
                <TicketLink
                    showing={showing}
                    content={content}
                    onClick={(e) => e.stopPropagation()}>
                    <MobileOrDesktopRenderer
                        desktopContent={
                            <span
                                className="inline-flex items-center gap-8 px-12 py-6 rounded-4 text-body-bold-14 cursor-pointer"
                                style={{
                                    backgroundColor: domainConfiguration.style.primaryColor,
                                    color: domainConfiguration.style.onPrimaryColor
                                }}>
                                <IconTicket2_24
                                    className="size-20"
                                    fill={domainConfiguration.style.onPrimaryColor}
                                />
                                {t('tickets.tickets')}
                            </span>
                        }
                        mobileContent={
                            <span
                                className="inline-flex items-center justify-center p-10 rounded-4 cursor-pointer"
                                style={{
                                    backgroundColor: domainConfiguration.style.primaryColor,
                                    color: domainConfiguration.style.onPrimaryColor
                                }}>
                                <IconTicket2_24
                                    className="size-20"
                                    fill={domainConfiguration.style.onPrimaryColor}
                                />
                            </span>
                        }
                    />
                </TicketLink>
            </div>

            {/* Highlight text row */}
            {showing.highlightText && (
                <div className="col-start-2 col-span-2 row-start-2 pr-4">
                    <span className="text-body-12 opacity-70 line-clamp-2">{showing.highlightText}</span>
                </div>
            )}

            {isInfoModalOpen && (
                <ShowingInfoModal
                    showing={showing}
                    content={content}
                    modalIsOpen={isInfoModalOpen}
                    setModalIsOpen={setIsInfoModalOpen}
                    specials={showingTags}
                    onClose={() => setIsInfoModalOpen(false)}
                    onShare={() => {}}
                    onSubmit={() => {}}
                />
            )}
        </div>
    );
}
